/**
 * Dashboard Stock is open for counting on every plan. Valuation, wastage,
 * and the variance email stay on Pro. Do not send Starter / Growth to the phone.
 */

import { shouldShowGuidedEmptyState } from './growthEngineRecommendation'
import { canUseStockTakeReports } from './stockTakePlanGate'

export const DESKTOP_STOCK_REPORTING_LOCKED_PLANS = Object.freeze([
  'Free',
  'Starter',
  'Growth',
])

export const STOCK_COUNT_TO_ORDER_JOB =
  'Count what is on the shelf, then order the gap to par from this take.'

export const STOCK_REPORTS_ON_PRO =
  'Valuation, wastage, and variance email reports are on Pro.'

export const FREE_STOCK_DASHBOARD_HONEST_NOTE = `${STOCK_COUNT_TO_ORDER_JOB} ${STOCK_REPORTS_ON_PRO}`

export const FREE_STOCK_DESKTOP_LEAD =
  'Count here, then order to par. Reports stay on Pro.'

export const FREE_STOCK_APP_TRY_TITLE = 'Count here, then order to par'

export const FREE_STOCK_APP_TRY_BODY =
  'OMS Lite includes 10 stock takes — enough for a small venue to run a couple of counts and see if it helps. Count what you have, then order the gap to par. Valuation, wastage, and variance email stay on Pro.'

export const FREE_STOCK_UPGRADE_CTA = 'Upgrade to Pro for Full Reporting'

export const FREE_STOCK_LIMIT_DESCRIPTION =
  'Valuation, wastage, and variance email on this screen are Pro — after you have counted and ordered to par.'

export const PAID_STOCK_LIMIT_DESCRIPTION =
  'Valuation, wastage, and variance email on this screen are Pro — reports on top of the counts you already do here.'

const normaliseStockPlan = plan => {
  const raw = String(plan || '').trim()
  return DESKTOP_STOCK_REPORTING_LOCKED_PLANS.find(
    name => name.toLowerCase() === raw.toLowerCase(),
  )
}

/**
 * Count-to-order job, plus a Pro reports line unless the venue is already Pro.
 *
 * @param {string} [plan]
 * @returns {string}
 */
export function getStockDashboardHonestNote(plan) {
  if (canUseStockTakeReports(plan)) return STOCK_COUNT_TO_ORDER_JOB
  const name = normaliseStockPlan(plan)
  if (name && name !== 'Free') {
    return `${STOCK_COUNT_TO_ORDER_JOB} ${STOCK_REPORTS_ON_PRO}`
  }
  return FREE_STOCK_DASHBOARD_HONEST_NOTE
}

/**
 * Empty list: count-to-order job, and Pro reports unless the venue is Pro.
 *
 * @param {string} [plan]
 * @param {{ fromWhy?: boolean }} [options]
 * @returns {string}
 */
export function getStockTakesListEmptyCopy(plan, { fromWhy = false } = {}) {
  const start = fromWhy
    ? 'No stock takes yet. Start from the why above.'
    : 'No stock takes yet.'
  const job = `${start} ${STOCK_COUNT_TO_ORDER_JOB}`
  if (canUseStockTakeReports(plan)) return job
  return `${job} ${STOCK_REPORTS_ON_PRO}`
}

/**
 * Reports paywall copy. Starter / Growth count on this screen — not the phone.
 *
 * @param {string} [plan]
 * @returns {{ tryBody: string, description: string }}
 */
export function getStockLimitPlanCopy(plan) {
  const name = normaliseStockPlan(plan)
  if (!name || name === 'Free') {
    return {
      tryBody: FREE_STOCK_APP_TRY_BODY,
      description: FREE_STOCK_LIMIT_DESCRIPTION,
    }
  }
  return {
    tryBody: `Your ${name} plan includes stock takes on this screen. Count what you have, then order the gap to par. Valuation, wastage, and variance email stay on Pro.`,
    description: PAID_STOCK_LIMIT_DESCRIPTION,
  }
}

/**
 * @deprecated Start is allowed on the dashboard for every plan.
 * Kept so existing callers still rewrite leftover phone copy if any.
 */
export function rewriteFreeStockDashboardCopy(text) {
  return String(text || '').trim()
}

export function isDesktopStockReportingLocked(plan) {
  return !canUseStockTakeReports(plan) && Boolean(normaliseStockPlan(plan))
}

/** Desktop can create and count on every plan — never hide Start for Stock. */
export function shouldBlockStockStartCta() {
  return false
}

export function shouldShowStockReportsHonestNote(plan) {
  return isDesktopStockReportingLocked(plan)
}

/**
 * First-try why from Express. Start stays; reports note is Pro-only unless Pro.
 */
export function getFreeStockDashboardEmptyProps(
  guidedSuccess,
  { currentPlan } = {},
) {
  if (!isDesktopStockReportingLocked(currentPlan)) return null
  const featureId = guidedSuccess?.featureId
  if (featureId && featureId !== 'stock_takes') return null
  if (!shouldShowGuidedEmptyState(guidedSuccess)) return null

  const emptyState = guidedSuccess.emptyState
  return {
    title: emptyState.title || '',
    body: emptyState.body || STOCK_COUNT_TO_ORDER_JOB,
    valueLine: emptyState.valueLine || null,
    estimatedMinutes: emptyState.estimatedMinutes ?? null,
    honestNote: getStockDashboardHonestNote(currentPlan),
    ctaLabel: emptyState.cta?.label || null,
    ctaAction: emptyState.cta?.action || null,
  }
}
